import { cardSurfaceClass } from "@/components/threads/shared.tsx";
import { cn, resolveApiPath } from "@/lib/utils.ts";
import { useQuery } from "@tanstack/react-query";
import { Lock, Pencil, Shield, Zap } from "lucide-react";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { routineQueries, useLabelConfig } from "../../hooks/useRoutines.ts";
import { connectorReauthPath } from "@/shared/connectors/reauth.ts";
import {
  classifierCopy,
  dateInputToEndsAt,
  endsAtToDateInput,
  isRsvpResponseStatus,
  rsvpResponseOptions,
  selectableTriggerTypes,
  slackChannelOptions,
  slackChannelPlaceholder,
  slackTriggerCopy,
  triggerTypeLabels,
} from "@/shared/routines.ts";
import type { Contact, Handle } from "../../types/contact.ts";
import {
  type EmailLabelColor,
  ROUTINE_AMENDMENT_MAX,
  ROUTINE_DESCRIPTION_MAX_LENGTH,
  type SlackTriggerAvailabilityResp,
  type TriggerConfig,
} from "../../types/routines.ts";
import { Button } from "../ui/button.tsx";
import { ColorDot } from "./LabelCatalogEditor.tsx";
import { getTriggerIcon } from "./triggerIcon.ts";
import {
  EmailFromPickerPopover,
  type FromOption,
  useEmailFromOptions,
} from "../artifacts/EmailFromPickerPopover.tsx";
import { ContactInput } from "../ui/contact-input.tsx";
import { Input } from "../ui/input.tsx";
import type { ParticipantLike } from "../ui/participant-pill.tsx";
import { ParticipantPill } from "../ui/participant-pill.tsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select.tsx";
import { Textarea } from "../ui/textarea.tsx";
import { Toggle } from "../ui/toggle.tsx";
import { CronScheduleEditor } from "../cron/CronScheduleEditor.tsx";

// --- Field sub-components ---

function emailToParticipantLike(email: string): ParticipantLike {
  return { id: email, name: email, value: email };
}

function extractEmail(contact: Contact): string | null {
  const emailHandle = contact.handles?.find((h) => h.type === "emailAddress");
  return emailHandle?.value ?? null;
}

function StringField({
  label,
  value,
  onChange,
  onBlur,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  onBlur?: () => void;
  placeholder?: string;
}) {
  return (
    <div className="flex items-center gap-2 mt-1.5">
      <span className="text-xs text-muted-foreground w-28 shrink-0">{label}</span>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className="h-7 text-xs"
        placeholder={placeholder ?? `Enter ${label.toLowerCase()}...`}
      />
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  onBlur,
}: {
  label: string;
  value: number | undefined;
  onChange: (val: number | undefined) => void;
  onBlur?: () => void;
}) {
  return (
    <div className="flex items-center gap-2 mt-1.5">
      <span className="text-xs text-muted-foreground w-28 shrink-0">{label}</span>
      <Input
        type="number"
        value={value?.toString() ?? ""}
        onChange={(e) => {
          const num = e.target.value === "" ? undefined : Number(e.target.value);
          onChange(num);
        }}
        onBlur={onBlur}
        className="h-7 text-xs w-24"
      />
    </div>
  );
}

function ContactArrayField({
  label,
  values,
  onUpdate,
  statsigPrefix,
}: {
  label: string;
  values: string[];
  onUpdate: (newValues: string[]) => void;
  statsigPrefix: string;
}) {
  const handleAdd = useCallback(
    (email: string) => {
      if (values.includes(email)) return;
      onUpdate([...values, email]);
    },
    [values, onUpdate],
  );

  const handleRemove = useCallback(
    (email: string) => {
      onUpdate(values.filter((v) => v !== email));
    },
    [values, onUpdate],
  );

  const handleSelectContact = useCallback(
    (contact: Contact) => {
      const email = extractEmail(contact);
      if (email) handleAdd(email);
    },
    [handleAdd],
  );

  return (
    <div className="flex items-start gap-2 mt-1.5">
      <span className="text-xs text-muted-foreground w-28 shrink-0 pt-1">{label}</span>
      <div className="flex flex-wrap items-center gap-1">
        {values.map((email) => (
          <ParticipantPill
            key={email}
            participant={emailToParticipantLike(email)}
            variant="email"
            canDelete
            onDelete={() => handleRemove(email)}
          />
        ))}
        <ContactInput
          onSelectContact={handleSelectContact}
          onSelectHandle={(_contact: Contact, handle: Handle) => {
            if (handle.value) handleAdd(handle.value);
          }}
          onSelectEmail={handleAdd}
          buttonClassName="h-6 w-6"
          statsigName={`${statsigPrefix}-add-${label.toLowerCase().replace(/\s/g, "-")}`}
        />
      </div>
    </div>
  );
}

function SingleContactField({
  label,
  value,
  onUpdate,
  statsigPrefix,
}: {
  label: string;
  value: string | undefined;
  onUpdate: (newValue: string | undefined) => void;
  statsigPrefix: string;
}) {
  const handleSelectContact = useCallback(
    (contact: Contact) => {
      const email = extractEmail(contact);
      if (email) onUpdate(email);
    },
    [onUpdate],
  );

  return (
    <div className="flex items-start gap-2 mt-1.5">
      <span className="text-xs text-muted-foreground w-28 shrink-0 pt-1">{label}</span>
      <div className="flex flex-wrap items-center gap-1">
        {value && (
          <ParticipantPill
            participant={emailToParticipantLike(value)}
            variant="email"
            canDelete
            onDelete={() => onUpdate(undefined)}
          />
        )}
        {!value && (
          <ContactInput
            onSelectContact={handleSelectContact}
            onSelectHandle={(_contact: Contact, handle: Handle) => {
              if (handle.value) onUpdate(handle.value);
            }}
            onSelectEmail={(email) => onUpdate(email)}
            buttonClassName="h-6 w-6"
            statsigName={`${statsigPrefix}-set-${label.toLowerCase().replace(/\s/g, "-")}`}
          />
        )}
      </div>
    </div>
  );
}

// Any-of classification-label picker, shared by both mail triggers. The user
// picks from their own auto-inbox catalog (built-in + custom labels); we store
// the label *names*, matching `source.classificationLabels` on the backend.
// Labels are the "action needed"-style signals auto-inbox stamps at extraction
// time.
//
// `required` drops the remove button on the last remaining label — the
// `emailLabeled` trigger's schema demands a non-empty list, and a form that lets
// you empty it would just fail to save with nothing to point at.
function LabelConditionField({
  values,
  onUpdate,
  caption = "With label",
  required = false,
}: {
  values: string[];
  onUpdate: (next: string[]) => void;
  caption?: string;
  required?: boolean;
}) {
  const { config } = useLabelConfig("auto-inbox");
  const catalog = useMemo(
    () => [...(config?.builtIns ?? []), ...(config?.customLabels ?? [])],
    [config],
  );
  const colorByName = useMemo(() => {
    const m = new Map<string, EmailLabelColor>();
    for (const l of catalog) m.set(l.name.toLowerCase(), l.color);
    return m;
  }, [catalog]);
  const available = catalog.filter(
    (l) => !values.some((v) => v.toLowerCase() === l.name.toLowerCase()),
  );

  return (
    <div className="flex items-start gap-2 mt-1.5">
      <span className="text-xs text-muted-foreground w-28 shrink-0 pt-1">{caption}</span>
      <div className="flex flex-wrap items-center gap-1">
        {values.map((name) => (
          <span
            key={name}
            className="inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-xs"
          >
            <ColorDot color={colorByName.get(name.toLowerCase()) ?? "gray"} />
            {name}
            {!(required && values.length === 1) && (
              <button
                type="button"
                aria-label={`Remove ${name}`}
                onClick={() => onUpdate(values.filter((v) => v !== name))}
                className="text-muted-foreground hover:text-foreground"
              >
                ×
              </button>
            )}
          </span>
        ))}
        {available.length > 0 && (
          <Select value="" onValueChange={(name) => onUpdate([...values, name])}>
            <SelectTrigger className="h-6 text-xs w-auto gap-1 rounded-md">
              <SelectValue placeholder="Add label" />
            </SelectTrigger>
            <SelectContent>
              {available.map((l) => (
                <SelectItem key={l.id} value={l.name}>
                  <div className="flex items-center gap-2">
                    <ColorDot color={l.color} />
                    {l.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
}

function ClassifierField({
  value,
  onChange,
  onBlur,
  triggerType,
}: {
  value: string;
  onChange: (val: string) => void;
  onBlur?: () => void;
  triggerType: TriggerConfig["type"];
}) {
  const { description, placeholder } = classifierCopy(triggerType);

  return (
    <div className="mt-1.5">
      <div className="text-xs text-muted-foreground mb-1">About</div>
      <div className="text-xs text-muted-foreground/70 mb-2">{description}</div>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => onBlur?.()}
        placeholder={placeholder}
        className="text-xs min-h-[96px]"
      />
    </div>
  );
}

// --- End time field ---

/**
 * When the routine stops. Sits with the trigger because "how long does this run"
 * is read alongside "when does it fire", even though the value lives on the
 * routine rather than in its trigger config — so it shows for every trigger
 * type, not just cron.
 *
 * Edited as a date and stored as an instant; see `dateInputToEndsAt`. Clearing
 * it is a first-class action, because an end time a user cannot remove is its
 * own trap.
 */
/**
 * Earliest date the field accepts. A routine that ended before okthx existed is
 * always a typo, and the browser's own `min` catches it before a save does.
 */
const MIN_END_DATE = "2020-01-01";

/**
 * Whether a `type="date"` input's value is a finished date rather than one
 * mid-keystroke. The element reports `YYYY-MM-DD` throughout, padding the year
 * as it fills, so the year is what distinguishes "done" from "still typing".
 */
const isCompleteDateInput = (value: string): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match !== null && Number(match[1]) >= 1000;
};

function EndTimeField({
  value,
  onChange,
  onBlur,
}: {
  value: string | undefined;
  onChange: (endsAt: string | undefined) => void;
  onBlur?: () => void;
}) {
  // Read inline rather than through `lib/user.ts`, whose module graph reaches
  // the Google Maps client and throws at import time without an API key.
  const timeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const dateValue = endsAtToDateInput(value, timeZone);

  // Hold what the user has typed, not what the stored instant converts back to.
  // A `type="date"` input reports each partial state as the segments fill —
  // "0006-06-15" while the year is still being typed — and feeding that through
  // the instant conversion and back overwrites the segment buffer, so typing a
  // date lands on the wrong year entirely. Only complete dates go upstream.
  const [typed, setTyped] = useState(dateValue);
  const [lastExternal, setLastExternal] = useState(dateValue);
  if (dateValue !== lastExternal) {
    // The stored value moved on its own — a save, a reset, the Remove button.
    // Tracked separately from what is typed so an unchanged parent never wipes
    // a half-entered date back out of the field.
    setLastExternal(dateValue);
    setTyped(dateValue);
  }

  return (
    <div className="flex items-center gap-2 py-1">
      <span className="text-sm text-muted-foreground w-24 shrink-0">Ends</span>
      <Input
        type="date"
        value={typed}
        min={MIN_END_DATE}
        onChange={(e) => {
          const next = e.target.value;
          setTyped(next);
          // An empty field clears the end time; a half-typed one is not a date
          // yet and must not be reported as one.
          if (next === "" || isCompleteDateInput(next)) {
            onChange(dateInputToEndsAt(next, timeZone));
          }
        }}
        onBlur={onBlur}
        className="h-8 text-sm w-auto rounded-md"
        aria-label="End date"
      />
      {dateValue ? (
        <button
          type="button"
          onClick={() => {
            onChange(undefined);
            onBlur?.();
          }}
          className="text-xs text-muted-foreground hover:text-foreground underline"
        >
          Remove
        </button>
      ) : (
        <span className="text-xs text-muted-foreground">Runs until you turn it off</span>
      )}
    </div>
  );
}

// --- Trigger detail fields ---

function EmailTriggerFields({
  config,
  onPatch,
  onBlur,
  statsigPrefix,
}: {
  config: Extract<TriggerConfig, { type: "email" }>;
  onPatch: (patch: Partial<TriggerConfig>) => void;
  onBlur?: () => void;
  statsigPrefix: string;
}) {
  return (
    <>
      <ContactArrayField
        label="From"
        values={config.fromSenders ?? []}
        onUpdate={(v) => onPatch({ fromSenders: v })}
        statsigPrefix={statsigPrefix}
      />
      <ContactArrayField
        label="To"
        values={config.toRecipients ?? []}
        onUpdate={(v) => onPatch({ toRecipients: v })}
        statsigPrefix={statsigPrefix}
      />
      <ContactArrayField
        label="CC"
        values={config.ccRecipients ?? []}
        onUpdate={(v) => onPatch({ ccRecipients: v })}
        statsigPrefix={statsigPrefix}
      />
      <LabelConditionField
        values={config.classificationLabels ?? []}
        onUpdate={(v) => onPatch({ classificationLabels: v.length > 0 ? v : undefined })}
      />
      <StringField
        label="Subject contains"
        value={config.subjectContains ?? ""}
        onChange={(v) => onPatch({ subjectContains: v || undefined })}
        onBlur={onBlur}
      />
      <ClassifierField
        value={config.classifierPrompt ?? ""}
        onChange={(v) => onPatch({ classifierPrompt: v || undefined })}
        onBlur={onBlur}
        triggerType="email"
      />
    </>
  );
}

/**
 * The "An email is labeled" trigger. Same header conditions as the email
 * trigger, but the labels lead — they are what the routine fires on, not a
 * filter over mail that arrived, so they read first and are not removable down
 * to nothing.
 */
function EmailLabeledTriggerFields({
  config,
  onPatch,
  onBlur,
  statsigPrefix,
}: {
  config: Extract<TriggerConfig, { type: "emailLabeled" }>;
  onPatch: (patch: Partial<TriggerConfig>) => void;
  onBlur?: () => void;
  statsigPrefix: string;
}) {
  return (
    <>
      <LabelConditionField
        caption="Labeled"
        required
        values={config.labels}
        onUpdate={(v) => onPatch({ labels: v })}
      />
      <ContactArrayField
        label="From"
        values={config.fromSenders ?? []}
        onUpdate={(v) => onPatch({ fromSenders: v })}
        statsigPrefix={statsigPrefix}
      />
      <ContactArrayField
        label="To"
        values={config.toRecipients ?? []}
        onUpdate={(v) => onPatch({ toRecipients: v })}
        statsigPrefix={statsigPrefix}
      />
      <ContactArrayField
        label="CC"
        values={config.ccRecipients ?? []}
        onUpdate={(v) => onPatch({ ccRecipients: v })}
        statsigPrefix={statsigPrefix}
      />
      <StringField
        label="Subject contains"
        value={config.subjectContains ?? ""}
        onChange={(v) => onPatch({ subjectContains: v || undefined })}
        onBlur={onBlur}
      />
      <ClassifierField
        value={config.classifierPrompt ?? ""}
        onChange={(v) => onPatch({ classifierPrompt: v || undefined })}
        onBlur={onBlur}
        triggerType="email"
      />
    </>
  );
}

/**
 * The "A Slack message is posted" trigger: which channel it watches, and what
 * the message should be about.
 *
 * The channel list comes from the user's own Slack connection, so it is only
 * asked for once the server says that connection can serve it. A connection
 * made before the trigger existed lacks the scopes to read channels, and gets a
 * Reconnect in place of the picker.
 */
function SlackTriggerFields({
  config,
  availability,
  onPatch,
  onBlur,
}: {
  config: Extract<TriggerConfig, { type: "slackMessage" }>;
  availability: SlackTriggerAvailabilityResp | undefined;
  onPatch: (patch: Partial<TriggerConfig>) => void;
  onBlur?: () => void;
}) {
  const ready = availability?.status === "ready";
  const { data, isLoading, isError } = useQuery(routineQueries.slackTriggerChannels(ready));
  const options = slackChannelOptions(data?.channels ?? [], config);

  let channelControl: ReactNode;
  if (availability?.status === "reconnect") {
    channelControl = (
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">{slackTriggerCopy.reconnect}</span>
        <Button variant="primary" size="xs" asChild data-statsig-name="routine-slack-reconnect">
          <a
            href={resolveApiPath(
              connectorReauthPath({ id: availability.connectorId, provider: "slack" }),
            )}
            target="_blank"
            rel="noopener noreferrer"
          >
            Reconnect
          </a>
        </Button>
      </div>
    );
  } else if (availability?.status === "ready") {
    const { teamId } = availability;
    channelControl = (
      <Select
        value={config.channelId || undefined}
        onValueChange={(id) => {
          const channel = options.find((c) => c.id === id);
          if (!channel) return;
          onPatch({ teamId, channelId: channel.id, channelName: channel.name });
          onBlur?.();
        }}
      >
        <SelectTrigger className="h-7 text-xs w-56 rounded-md">
          <SelectValue placeholder={slackChannelPlaceholder({ isLoading, isError })} />
        </SelectTrigger>
        <SelectContent>
          {options.map((channel) => (
            <SelectItem key={channel.id} value={channel.id}>
              <div className="flex items-center gap-1.5">
                #{channel.name}
                {channel.isPrivate && (
                  <Lock className="size-3 text-muted-foreground" aria-label="Private channel" />
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  } else {
    // No Slack connection to pick with — still loading, or the form is editing
    // a routine on someone else's behalf. The saved channel reads as text.
    channelControl = (
      <span className="text-xs">
        {config.channelId ? `#${config.channelName}` : slackTriggerCopy.notConnected}
      </span>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2 mt-1.5">
        <span className="text-xs text-muted-foreground w-28 shrink-0">Channel</span>
        {channelControl}
      </div>
      <ClassifierField
        value={config.classifierPrompt ?? ""}
        onChange={(v) => onPatch({ classifierPrompt: v || undefined })}
        onBlur={onBlur}
        triggerType="slackMessage"
      />
    </>
  );
}

function CalendarSharedFields({
  config,
  onPatch,
  onBlur,
  statsigPrefix,
}: {
  config: TriggerConfig & {
    participants?: string[];
    organizer?: string;
    subjectContains?: string;
    minDurationMinutes?: number;
    maxDurationMinutes?: number;
    classifierPrompt?: string;
  };
  onPatch: (patch: Partial<TriggerConfig>) => void;
  onBlur?: () => void;
  statsigPrefix: string;
}) {
  return (
    <>
      <ContactArrayField
        label="Participants"
        values={config.participants ?? []}
        onUpdate={(v) => onPatch({ participants: v })}
        statsigPrefix={statsigPrefix}
      />
      <SingleContactField
        label="Organizer"
        value={config.organizer}
        onUpdate={(v) => onPatch({ organizer: v })}
        statsigPrefix={statsigPrefix}
      />
      <StringField
        label="Subject contains"
        value={config.subjectContains ?? ""}
        onChange={(v) => onPatch({ subjectContains: v || undefined })}
        onBlur={onBlur}
      />
      <NumberField
        label="Min duration (min)"
        value={config.minDurationMinutes}
        onChange={(v) => onPatch({ minDurationMinutes: v })}
        onBlur={onBlur}
      />
      <NumberField
        label="Max duration (min)"
        value={config.maxDurationMinutes}
        onChange={(v) => onPatch({ maxDurationMinutes: v })}
        onBlur={onBlur}
      />
      <ClassifierField
        value={config.classifierPrompt ?? ""}
        onChange={(v) => onPatch({ classifierPrompt: v || undefined })}
        onBlur={onBlur}
        triggerType={config.type}
      />
    </>
  );
}

function TriggerDetailFields({
  triggerConfig,
  slackTriggerAvailability,
  onPatch,
  onBlur,
  statsigPrefix,
}: {
  triggerConfig: TriggerConfig;
  slackTriggerAvailability: SlackTriggerAvailabilityResp | undefined;
  onPatch: (patch: Partial<TriggerConfig>) => void;
  onBlur?: () => void;
  statsigPrefix: string;
}) {
  switch (triggerConfig.type) {
    case "email":
      return (
        <EmailTriggerFields
          config={triggerConfig}
          onPatch={onPatch}
          onBlur={onBlur}
          statsigPrefix={statsigPrefix}
        />
      );
    case "emailLabeled":
      return (
        <EmailLabeledTriggerFields
          config={triggerConfig}
          onPatch={onPatch}
          onBlur={onBlur}
          statsigPrefix={statsigPrefix}
        />
      );
    case "beforeMeeting":
      return (
        <>
          <NumberField
            label="How long before (min)"
            value={triggerConfig.minutesBefore}
            onChange={(v) => onPatch({ minutesBefore: v ?? 0 })}
            onBlur={onBlur}
          />
          <CalendarSharedFields
            config={triggerConfig}
            onPatch={onPatch}
            onBlur={onBlur}
            statsigPrefix={statsigPrefix}
          />
        </>
      );
    case "afterMeeting":
      return (
        <>
          <NumberField
            label="How long after (min)"
            value={triggerConfig.minutesAfter}
            onChange={(v) => onPatch({ minutesAfter: v ?? 0 })}
            onBlur={onBlur}
          />
          <CalendarSharedFields
            config={triggerConfig}
            onPatch={onPatch}
            onBlur={onBlur}
            statsigPrefix={statsigPrefix}
          />
        </>
      );
    case "rsvpResponse":
      return (
        <>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-xs text-muted-foreground w-28 shrink-0">Response status</span>
            <Select
              value={triggerConfig.responseStatus}
              // Radix hands back a bare string, so the answer is narrowed rather
              // than asserted; anything unrecognized leaves the filter alone.
              onValueChange={(v) => {
                if (isRsvpResponseStatus(v)) onPatch({ responseStatus: v });
              }}
            >
              <SelectTrigger className="h-7 text-xs w-36 rounded-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {rsvpResponseOptions.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <CalendarSharedFields
            config={triggerConfig}
            onPatch={onPatch}
            onBlur={onBlur}
            statsigPrefix={statsigPrefix}
          />
        </>
      );
    case "inviteReceived":
    case "inviteUpdated":
    case "eventCancelled":
      return (
        <CalendarSharedFields
          config={triggerConfig}
          onPatch={onPatch}
          onBlur={onBlur}
          statsigPrefix={statsigPrefix}
        />
      );
    case "slackMessage":
      return (
        <SlackTriggerFields
          config={triggerConfig}
          availability={slackTriggerAvailability}
          onPatch={onPatch}
          onBlur={onBlur}
        />
      );
    // Set up only by the agent; the form edits just the classifier.
    case "slackDirectMessage":
    case "slackMention":
      return (
        <ClassifierField
          value={triggerConfig.classifierPrompt ?? ""}
          onChange={(v) => onPatch({ classifierPrompt: v || undefined })}
          onBlur={onBlur}
          triggerType={triggerConfig.type}
        />
      );
    case "cron":
      return (
        <CronScheduleEditor
          value={triggerConfig.schedule}
          onChange={(v) => onPatch({ schedule: v })}
        />
      );
  }
}

// --- Main shared form component ---

export type RoutineFormData = {
  name: string;
  prompt: string;
  triggerConfig: TriggerConfig;
};

/**
 * Shared routine form fields, used by `NewRoutineForm`, `RoutineDetail`, and the
 * admin routine editor. Purely controlled — the caller manages all state.
 *
 * @param onFieldBlur - Optional callback fired when a text field loses focus, for
 *   callers that save on blur rather than through an explicit Save button.
 */
export const RoutineFormFields = ({
  name,
  onNameChange,
  description,
  onDescriptionChange,
  prompt,
  onPromptChange,
  onPromptSave,
  amendment,
  onAmendmentChange,
  isSystemRoutine = false,
  triggerConfig,
  onTriggerConfigChange,
  onTriggerTypeChange,
  slackTriggerAvailability,
  endsAt,
  onEndsAtChange,
  autoSendCommunication,
  onAutoSendCommunicationChange,
  autoArchive,
  onAutoArchiveChange,
  defaultFrom,
  onDefaultFromChange,
  onFieldBlur,
  statsigPrefix = "routine-form",
  showPermissions = true,
  showEndTime = true,
  showName = true,
}: {
  name: string;
  onNameChange: (name: string) => void;
  description: string;
  onDescriptionChange: (description: string) => void;
  prompt: string;
  onPromptChange: (prompt: string) => void;
  onPromptSave?: (prompt: string) => Promise<void>;
  // A routine okthx ships: `prompt` renders read-only and `amendment` is the
  // editable field. A routine the user wrote has neither — `prompt` is theirs.
  amendment?: string;
  onAmendmentChange?: (amendment: string) => void;
  isSystemRoutine?: boolean;
  triggerConfig: TriggerConfig;
  onTriggerConfigChange: (config: TriggerConfig) => void;
  onTriggerTypeChange: (type: TriggerConfig["type"]) => void;
  /**
   * Whether this user can set up a Slack trigger. Left undefined where the form
   * edits someone else's routine (the admin editor): the Slack option is then
   * offered only on a routine that already has it, and its channel is read-only.
   */
  slackTriggerAvailability?: SlackTriggerAvailabilityResp;
  /** Undefined when the routine runs until the user turns it off. */
  endsAt?: string;
  onEndsAtChange: (endsAt: string | undefined) => void;
  autoSendCommunication: boolean;
  onAutoSendCommunicationChange: (value: boolean) => void;
  autoArchive: boolean;
  onAutoArchiveChange: (value: boolean) => void;
  defaultFrom: "user" | "assistant";
  onDefaultFromChange: (value: "user" | "assistant") => void;
  onFieldBlur?: (
    field:
      | "name"
      | "description"
      | "prompt"
      | "amendment"
      | "triggerConfig"
      | "permissions"
      | "endsAt",
  ) => void;
  statsigPrefix?: string;
  // When false, the Permissions section is omitted — the tabbed editors render
  // `RoutinePermissionsFields` themselves, in their own tab. Defaults to true so
  // the untabbed create form keeps everything inline.
  showPermissions?: boolean;
  // False for a routine okthx ships: those carry no end time, and the server
  // refuses one. Turning the routine off is the bound it offers instead.
  showEndTime?: boolean;
  // Defaults to true. A routine okthx ships turns it off: the panel header
  // already states its name.
  showName?: boolean;
}) => {
  const TriggerIcon = getTriggerIcon(triggerConfig.type);

  const handleTriggerPatch = useCallback(
    (patch: Partial<TriggerConfig>) => {
      onTriggerConfigChange({ ...triggerConfig, ...patch } as TriggerConfig);
    },
    [triggerConfig, onTriggerConfigChange],
  );

  return (
    <>
      {/* Name */}
      <div>
        {showName &&
          // Read-only on a routine okthx ships, for the same reason as the
          // description and prompt: the name is okthx's, and an edit to it is
          // dropped on save rather than stored.
          (isSystemRoutine ? (
            <p className="px-2 py-1.5 text-base font-semibold">{name}</p>
          ) : (
            <div className="flex items-center gap-2">
              <Input
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                onBlur={() => onFieldBlur?.("name")}
                className="h-auto py-1.5 text-base font-semibold border-none shadow-none px-2 focus-visible:ring-1 focus-visible:ring-offset-0 rounded-md"
                placeholder="Routine name..."
              />
            </div>
          ))}
        {/* The line the user reads in the routine list to decide whether to keep
            this routine on — not the instruction it follows, which is "Then do"
            below.

            Read-only on a routine okthx ships, like the prompt further down: it
            describes what that prompt does, and the prompt is not the user's to
            change, so rewording it could only make the list disagree with the
            routine. Editable on a routine the user wrote, where the words are
            theirs — bordered and two lines tall, since it runs to a couple of
            sentences and a single line would scroll the rest out of view. */}
        {isSystemRoutine ? (
          description && <p className="text-sm text-muted-foreground px-2">{description}</p>
        ) : (
          <Textarea
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            onBlur={() => onFieldBlur?.("description")}
            maxLength={ROUTINE_DESCRIPTION_MAX_LENGTH}
            className="text-sm text-muted-foreground min-h-[72px]"
            placeholder="Add a one-line description..."
          />
        )}
      </div>

      <div className="h-px bg-border" />

      {/* When */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <TriggerIcon className="size-3.5 text-muted-foreground" />
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            When
          </span>
        </div>
        <div className="flex items-center gap-2 mb-2">
          <Select
            value={triggerConfig.type}
            onValueChange={(v) => onTriggerTypeChange(v as TriggerConfig["type"])}
          >
            <SelectTrigger className="h-8 text-sm w-auto gap-1.5 rounded-md">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {selectableTriggerTypes(slackTriggerAvailability, triggerConfig.type).map((type) => (
                <SelectItem key={type} value={type}>
                  {triggerTypeLabels[type]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {true && (
          <div className="pl-1 space-y-0.5">
            <TriggerDetailFields
              triggerConfig={triggerConfig}
              slackTriggerAvailability={slackTriggerAvailability}
              onPatch={handleTriggerPatch}
              onBlur={() => onFieldBlur?.("triggerConfig")}
              statsigPrefix={statsigPrefix}
            />
            {showEndTime && (
              <EndTimeField
                value={endsAt}
                onChange={onEndsAtChange}
                onBlur={() => onFieldBlur?.("endsAt")}
              />
            )}
          </div>
        )}
      </div>

      {/* A routine okthx ships doesn't show its prompt at all: it is okthx's
          text, kept up to date upstream, and the user can neither edit it nor
          act on it. What they can act on is their own instructions below. The
          divider goes with it — the amendment section brings its own. */}
      {!isSystemRoutine && (
        <>
          <div className="h-px bg-border" />

          <div>
            <div className="flex items-center gap-2 mb-3">
              <Zap className="size-3.5 text-muted-foreground" />
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Then do
              </span>
            </div>
            <Textarea
              value={prompt}
              onChange={(e) => onPromptChange(e.target.value)}
              onBlur={async (e) => {
                await onPromptSave?.(e.target.value);
              }}
              className="text-sm min-h-[320px]"
            />
          </div>
        </>
      )}

      {isSystemRoutine && (
        <>
          <div className="h-px bg-border" />
          <RoutineAmendmentSection
            amendment={amendment}
            onChange={onAmendmentChange}
            onBlur={() => onFieldBlur?.("amendment")}
          />
        </>
      )}

      {showPermissions && (
        <>
          <div className="h-px bg-border" />
          <RoutinePermissionsFields
            autoSendCommunication={autoSendCommunication}
            onAutoSendCommunicationChange={onAutoSendCommunicationChange}
            autoArchive={autoArchive}
            onAutoArchiveChange={onAutoArchiveChange}
            defaultFrom={defaultFrom}
            onDefaultFromChange={onDefaultFromChange}
            onFieldBlur={onFieldBlur}
            statsigPrefix={statsigPrefix}
          />
        </>
      )}
    </>
  );
};

/**
 * The user's own instructions for a routine okthx ships — appended to the
 * shipped prompt on every run rather than replacing it.
 *
 * The shipped prompt itself is not on screen, so the help copy names what these
 * instructions win over rather than pointing at a section above.
 */
export const RoutineAmendmentSection = ({
  amendment,
  onChange,
  onBlur,
}: {
  amendment?: string;
  onChange?: (amendment: string) => void;
  onBlur?: () => void;
}) => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      <Pencil className="size-3.5 text-muted-foreground" />
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Your instructions
      </span>
    </div>
    <p className="text-xs text-muted-foreground mb-2">
      Added every time this routine runs. Where these disagree with how Okthx wrote the routine,
      these win.
    </p>
    <Textarea
      value={amendment ?? ""}
      onChange={(e) => onChange?.(e.target.value)}
      onBlur={onBlur}
      maxLength={ROUTINE_AMENDMENT_MAX}
      placeholder={"- Put the weather at the top."}
      className="text-sm min-h-[120px]"
    />
    <div className="mt-1 text-right text-xs text-muted-foreground">
      {(amendment ?? "").length.toLocaleString()} / {ROUTINE_AMENDMENT_MAX.toLocaleString()}
    </div>
  </div>
);

/**
 * The Permissions section — auto-send, auto-archive, and the Default From
 * picker. Extracted so the routine detail view can render it in its own
 * Permissions tab while the untabbed create form keeps it inline (via
 * `RoutineFormFields`'s `showPermissions`).
 */
export const RoutinePermissionsFields = ({
  autoSendCommunication,
  onAutoSendCommunicationChange,
  autoArchive,
  onAutoArchiveChange,
  defaultFrom,
  onDefaultFromChange,
  onFieldBlur,
  statsigPrefix = "routine-form",
}: {
  autoSendCommunication: boolean;
  onAutoSendCommunicationChange: (value: boolean) => void;
  autoArchive: boolean;
  onAutoArchiveChange: (value: boolean) => void;
  defaultFrom: "user" | "assistant";
  onDefaultFromChange: (value: "user" | "assistant") => void;
  onFieldBlur?: (field: "permissions") => void;
  statsigPrefix?: string;
}) => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      <Shield className="size-3.5 text-muted-foreground" />
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Permissions
      </span>
    </div>
    <div className="flex flex-col gap-2">
      <PermissionToggle
        label="Auto-send communications"
        onText="Emails and messages will be sent automatically without approval."
        offText="You'll be asked to approve before any communication is sent."
        value={autoSendCommunication}
        onChange={(v) => {
          onAutoSendCommunicationChange(v);
          onFieldBlur?.("permissions");
        }}
      />
      <PermissionToggle
        label="Auto-archive when done"
        onText="The thread will be archived automatically once the routine completes."
        offText="The thread stays open after the routine completes."
        value={autoArchive}
        onChange={(v) => {
          onAutoArchiveChange(v);
          onFieldBlur?.("permissions");
        }}
      />
      <DefaultFromRow
        value={defaultFrom}
        onChange={(v) => {
          onDefaultFromChange(v);
          onFieldBlur?.("permissions");
        }}
        statsigPrefix={statsigPrefix}
      />
    </div>
  </div>
);

// --- Permission sub-components ---

// Single permission row with a slide toggle on the right. Used for both
// auto-send-communications and auto-archive — same shape, same styling, the
// only difference is the label / help text.
const PermissionToggle = ({
  label,
  onText,
  offText,
  value,
  onChange,
}: {
  label: string;
  onText: string;
  offText: string;
  value: boolean;
  onChange: (next: boolean) => void;
}) => (
  <div className={cn(cardSurfaceClass, "flex w-full items-center justify-between")}>
    <div className="text-left">
      <div className="text-sm font-medium">{label}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{value ? onText : offText}</div>
    </div>
    <Toggle checked={value} onCheckedChange={onChange} />
  </div>
);

// Mirrors the email-draft artifact's From picker: a pill of the resolved
// sender contact opens a dropdown with the user / assistant options.
// Degrades to a non-clickable display when the user context isn't loaded
// yet (e.g., either side's email handle is missing).
const DefaultFromRow = ({
  value,
  onChange,
  statsigPrefix,
}: {
  value: "user" | "assistant";
  onChange: (next: "user" | "assistant") => void;
  statsigPrefix: string;
}) => {
  const { userOption, assistantOption } = useEmailFromOptions();
  const selected = value === "assistant" ? assistantOption : userOption;

  const handleSelect = useCallback(
    (option: FromOption) => {
      onChange(option.isAssistant ? "assistant" : "user");
      return undefined;
    },
    [onChange],
  );

  const pill = selected ? (
    <ParticipantPill
      participant={{
        id: selected.handleId,
        name: selected.contactName,
        value: selected.handleValue,
        picture: selected.contactPicture,
      }}
      variant="email"
      disableContactSidebar
    />
  ) : (
    <span className="text-xs text-muted-foreground">Loading…</span>
  );

  return (
    <div className={cn(cardSurfaceClass, "flex w-full items-center justify-between")}>
      <div className="text-left">
        <div className="text-sm font-medium">Default From</div>
        <div className="text-xs text-muted-foreground mt-0.5">
          Who outgoing communications appear to come from when this routine runs.
        </div>
      </div>
      <span data-statsig-name={`${statsigPrefix}-default-from`}>
        {selected ? (
          <EmailFromPickerPopover
            trigger={pill}
            selectedHandleId={selected.handleId}
            onSelect={handleSelect}
          />
        ) : (
          pill
        )}
      </span>
    </div>
  );
};
